import { useReducedMotion } from 'motion/react';
import { type CSSProperties, type MutableRefObject, useEffect, useRef, useState } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { animateWorkspaceAgentOnboardingParkScroll } from '~/components/v2/components/workspace/workspace-agent-panel/utils/animateWorkspaceAgentOnboardingParkScroll';
import { getWorkspaceAgentHybridTurnParkMetrics } from '~/components/v2/components/workspace/workspace-agent-panel/utils/getWorkspaceAgentHybridTurnParkMetrics';
import { getWorkspaceAgentOnboardingPrefixScrollMargin } from '~/components/v2/components/workspace/workspace-agent-panel/utils/getWorkspaceAgentOnboardingPrefixScrollMargin';
import { useWorkspaceAgentPanelStore } from '~/components/v2/store/features/workspace/useWorkspaceAgentPanelStore';

export type WorkspaceAgentHybridTurnLayout = {
  minHeight: number;
  scrollMargin: number;
};

const WORKSPACE_AGENT_HYBRID_TURN_LAYOUT_RESET: WorkspaceAgentHybridTurnLayout = {
  minHeight: 0,
  scrollMargin: 0,
};

const HYBRID_TURN_PARK_SCROLL_MS = 700;

type UseWorkspaceAgentHybridTurnParkLayoutParams = {
  displayRowCount: number;
  scrollContainerRef: MutableRefObject<HTMLElement | null>;
  suppressMeasureTailPinRef: MutableRefObject<boolean | undefined>;
};

export const useWorkspaceAgentHybridTurnParkLayout = ({
  displayRowCount,
  scrollContainerRef,
  suppressMeasureTailPinRef,
}: UseWorkspaceAgentHybridTurnParkLayoutParams) => {
  const shouldReduceMotion = !!useReducedMotion();
  const isHybridTurnParked = useWorkspaceAgentPanelStore((state) => state.isOnboardingHybridTurnLineVisible);
  const onboardingPrefixRef = useRef<HTMLDivElement | null>(null);
  const parkScrollAnimationCancelRef = useRef<(() => void) | undefined>(undefined);
  const isParkScrollAnimatingRef = useRef<boolean>();
  const hasPlayedParkScrollAnimationRef = useRef<boolean>();
  const [hybridTurnLayout, setHybridTurnLayout] = useState(WORKSPACE_AGENT_HYBRID_TURN_LAYOUT_RESET);

  const cancelParkScrollAnimation = () => {
    parkScrollAnimationCancelRef.current?.();
    parkScrollAnimationCancelRef.current = undefined;
    isParkScrollAnimatingRef.current = false;
  };

  const applyParkScrollTop = (container: HTMLElement, targetScrollTop: number, animate: boolean) => {
    cancelParkScrollAnimation();

    if (!animate || shouldReduceMotion) {
      container.scrollTop = targetScrollTop;
      return;
    }

    isParkScrollAnimatingRef.current = true;
    parkScrollAnimationCancelRef.current = animateWorkspaceAgentOnboardingParkScroll({
      durationMs: HYBRID_TURN_PARK_SCROLL_MS,
      element: container,
      onComplete: () => {
        isParkScrollAnimatingRef.current = false;
        parkScrollAnimationCancelRef.current = undefined;
      },
      targetScrollTop,
    });
  };

  if (isHybridTurnParked) {
    // An unpinned thread lets the virtualizer nudge scroll when the new row is measured.
    suppressMeasureTailPinRef.current = true;
  }

  useEffect(() => {
    if (isHybridTurnParked) {
      return () => {
        suppressMeasureTailPinRef.current = false;
      };
    }

    hasPlayedParkScrollAnimationRef.current = false;
    parkScrollAnimationCancelRef.current?.();
    parkScrollAnimationCancelRef.current = undefined;
    isParkScrollAnimatingRef.current = false;

    return undefined;
  }, [isHybridTurnParked, suppressMeasureTailPinRef]);

  useIsomorphicLayoutEffect(() => {
    const container = scrollContainerRef.current;
    const prefix = onboardingPrefixRef.current;
    const line = prefix?.querySelector('[data-workspace-agent-onboarding-turn-line]');
    const canPark = isHybridTurnParked && !!container && line instanceof HTMLElement;

    if (!canPark) {
      // Sharing and performance never park, but the scripted demo still sits above live rows.
      // The virtualizer needs that offset or those rows are measured off the top of the scrollport.
      const scrollMargin =
        !isHybridTurnParked && container && prefix && displayRowCount > 0
          ? getWorkspaceAgentOnboardingPrefixScrollMargin({ prefix, scrollContainer: container })
          : 0;

      setHybridTurnLayout((current) =>
        current.minHeight === 0 && Math.abs(current.scrollMargin - scrollMargin) <= 1
          ? current
          : { minHeight: 0, scrollMargin },
      );

      if (!container || !prefix || scrollMargin === 0) {
        return;
      }

      const observer = new ResizeObserver(() => {
        const nextScrollMargin = getWorkspaceAgentOnboardingPrefixScrollMargin({
          prefix,
          scrollContainer: container,
        });

        setHybridTurnLayout((current) =>
          current.minHeight !== 0 || Math.abs(current.scrollMargin - nextScrollMargin) <= 1
            ? current
            : { minHeight: 0, scrollMargin: nextScrollMargin },
        );
      });

      observer.observe(prefix);

      return () => observer.disconnect();
    }

    if (!container || !prefix || !(line instanceof HTMLElement)) {
      return;
    }

    const { lineTop, minHeight } = getWorkspaceAgentHybridTurnParkMetrics({ container, line });
    const scrollMargin = getWorkspaceAgentOnboardingPrefixScrollMargin({ prefix, scrollContainer: container });

    if (
      Math.abs(hybridTurnLayout.minHeight - minHeight) > 1 ||
      Math.abs(hybridTurnLayout.scrollMargin - scrollMargin) > 1
    ) {
      setHybridTurnLayout({ minHeight, scrollMargin });
      return;
    }

    // A follow-up layout pass used to snap scrollTop and cancel the in-flight park.
    if (!isParkScrollAnimatingRef.current && Math.abs(container.scrollTop - lineTop) > 1) {
      const shouldAnimateParkScroll = !hasPlayedParkScrollAnimationRef.current;

      applyParkScrollTop(container, lineTop, shouldAnimateParkScroll);
      hasPlayedParkScrollAnimationRef.current = true;
    }

    const observer = new ResizeObserver(() => {
      if (isParkScrollAnimatingRef.current) {
        return;
      }

      const nextLine = onboardingPrefixRef.current?.querySelector('[data-workspace-agent-onboarding-turn-line]');

      if (!(nextLine instanceof HTMLElement)) {
        return;
      }

      const { lineTop: nextLineTop } = getWorkspaceAgentHybridTurnParkMetrics({ container, line: nextLine });

      if (Math.abs(container.scrollTop - nextLineTop) > 1) {
        container.scrollTop = nextLineTop;
      }
    });

    observer.observe(container);

    return () => observer.disconnect();
  }, [
    displayRowCount,
    hybridTurnLayout.minHeight,
    hybridTurnLayout.scrollMargin,
    isHybridTurnParked,
    scrollContainerRef,
    shouldReduceMotion,
  ]);

  const chatScrollContainerStyle: CSSProperties | undefined = isHybridTurnParked
    ? { overflowAnchor: 'none' }
    : undefined;

  return {
    chatScrollContainerStyle,
    hybridTurnLayout,
    isHybridTurnParked,
    onboardingPrefixRef,
  };
};
